import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AffiliateInviteRequest,
  AffiliateList,
  AffiliateSettingsView,
  AffiliateSettingsWriteRequest,
} from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface AffiliatesData {
  list: AffiliateList;
  /** `null` when attribution settings failed or have not loaded yet. */
  settings: AffiliateSettingsView | null;
}

/** The settings load. A failure leaves the affiliate list up. */
export interface SettingsPart {
  status: 'loading' | 'ready' | 'error';
  error: unknown;
  reload: () => void;
}

export interface AffiliatesState {
  /** The list. Settings have their own status. */
  status: 'loading' | 'ready' | 'error';
  data: AffiliatesData | null;
  error: ApiError | null;
  reload: () => void;
  settings: SettingsPart;
  invite: (body: AffiliateInviteRequest) => Promise<void>;
  updateSettings: (body: AffiliateSettingsWriteRequest) => Promise<void>;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/**
 * Loads the affiliate list and attribution settings beside each other. A settings failure leaves
 * the list up (TMMRSL-0055).
 */
export function useAffiliates(): AffiliatesState {
  const [status, setStatus] = useState<AffiliatesState['status']>('loading');
  const [list, setList] = useState<AffiliateList | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [settings, setSettings] = useState<AffiliateSettingsView | null>(null);
  const [settingsStatus, setSettingsStatus] = useState<SettingsPart['status']>('loading');
  const [settingsError, setSettingsError] = useState<unknown>(null);
  const api = useTenantApi();
  const started = useRef(false);

  const reload = useCallback(() => {
    setStatus('loading');
    setError(null);
    api.listAffiliates().then(
      (next) => {
        setList(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        setList(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api]);

  const reloadSettings = useCallback(() => {
    setSettingsStatus('loading');
    setSettingsError(null);
    api.getAffiliateSettings().then(
      (next) => {
        setSettings(next);
        setSettingsError(null);
        setSettingsStatus('ready');
      },
      (err: unknown) => {
        setSettings(null);
        setSettingsError(err);
        setSettingsStatus('error');
      },
    );
  }, [api]);

  const invite = useCallback(
    async (body: AffiliateInviteRequest) => {
      await api.inviteAffiliate(body);
      const next = await api.listAffiliates();
      setList(next);
      setError(null);
      setStatus('ready');
    },
    [api],
  );

  const updateSettings = useCallback(
    async (body: AffiliateSettingsWriteRequest) => {
      const next = await api.putAffiliateSettings(body);
      setSettings(next);
      setSettingsError(null);
      setSettingsStatus('ready');
    },
    [api],
  );

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
    reloadSettings();
  }, [reload, reloadSettings]);

  const data: AffiliatesData | null = list ? { list, settings } : null;

  return {
    status,
    data,
    error,
    reload,
    settings: { status: settingsStatus, error: settingsError, reload: reloadSettings },
    invite,
    updateSettings,
  };
}
